if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      shippingCodes = new Set();

      connectedCallback() {
        this.querySelector('form')?.addEventListener('submit', this.applyDiscount.bind(this));
        this.querySelectorAll('[data-remove-discount]').forEach((btn) =>
          btn.addEventListener('click', this.removeDiscount.bind(this))
        );
      }

      get existingDiscounts() {
        return Array.from(this.querySelectorAll('[data-discount-code]'))
          .map((el) => el.dataset.discountCode)
          .filter(Boolean);
      }

      async applyDiscount(event) {
        event.preventDefault();
        const input = this.querySelector('input[name="discount"]');
        if (!input) return;

        const code = input.value.trim();
        if (!code) return;

        const upper = code.toUpperCase();
        if (this.existingDiscounts.some((c) => c.toUpperCase() === upper)) return;

        this.setLoading(true);
        this.hideError();

        try {
          const existing = this.existingDiscounts;
          const response = await fetch(window.routes.cart_update_url, {
            ...fetchConfig('json'),
            body: JSON.stringify({ discount: [...existing, code].join(',') }),
          });
          const data = await response.json();

          // A code that doesn't exist in discount_codes at all is truly invalid.
          // A code with applicable: false could be a minimum-not-met or expired code — show error.
          // Shipping discount codes come back as applicable: true.
          const codeEntry = (data.discount_codes || []).find(
            (d) => d.code.toUpperCase() === upper
          );

          if (!codeEntry || codeEntry.applicable === false) {
            this.showError();
            input.value = '';
          } else {
            input.value = '';
            const applicable = (data.discount_codes || [])
              .filter((d) => d.applicable)
              .map((d) => d.code);
            this.renderPills(applicable);

            // Detect shipping codes: they don't reduce cart total so won't appear
            // in cart_level_discount_applications or line_level_discount_allocations
            const hasCartLevel = (data.cart_level_discount_applications || []).some(
              (app) => app.title?.toUpperCase() === upper
            );
            const hasLineLevel = (data.items || []).some((item) =>
              (item.line_level_discount_allocations || []).some(
                (alloc) => alloc.discount_application?.title?.toUpperCase() === upper
              )
            );
            if (!hasCartLevel && !hasLineLevel) {
              this.shippingCodes.add(upper);
              this.showCouponNote();
            }

            await this.refreshTotals(data.total_price);
          }
        } catch {
          this.showError();
        } finally {
          this.setLoading(false);
        }
      }

      async removeDiscount(event) {
        event.preventDefault();
        const code = event.currentTarget.dataset.removeDiscount;
        if (!code) return;

        const remaining = this.existingDiscounts.filter(
          (c) => c.toUpperCase() !== code.toUpperCase()
        );

        this.setLoading(true);

        try {
          const response = await fetch(window.routes.cart_update_url, {
            ...fetchConfig('json'),
            body: JSON.stringify({ discount: remaining.join(',') }),
          });
          const data = await response.json();

          const applicable = (data.discount_codes || [])
            .filter((d) => d.applicable)
            .map((d) => d.code);
          this.renderPills(applicable);

          this.shippingCodes.delete(code.toUpperCase());
          if (this.shippingCodes.size === 0) this.hideCouponNote();

          await this.refreshTotals(data.total_price);
        } catch {
          /* silently fail */
        } finally {
          this.setLoading(false);
        }
      }

      renderPills(codes) {
        let container = this.querySelector('.cart-discount-pills');

        if (codes.length === 0) {
          container?.remove();
          return;
        }

        if (!container) {
          container = document.createElement('ul');
          container.className = 'cart-discount-pills list-unstyled';
          this.querySelector('.cart-discount-body')?.appendChild(container);
        }

        container.innerHTML = codes
          .map((code) => {
            const safe = code
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/"/g, '&quot;');
            return `<li class="cart-discount-pill" data-discount-code="${safe}">
              <span>${safe}</span>
              <button type="button" class="cart-discount-pill__remove" data-remove-discount="${safe}" aria-label="Remove ${safe}">&#x2715;</button>
            </li>`;
          })
          .join('');

        container.querySelectorAll('[data-remove-discount]').forEach((btn) =>
          btn.addEventListener('click', this.removeDiscount.bind(this))
        );

        // Always keep the note as the last child, below the pills
        const note = this.querySelector('.cart-free-shipping__coupon-note');
        if (note) this.querySelector('.cart-discount-body')?.appendChild(note);

        const details = this.querySelector('details');
        if (details) details.open = true;
      }

      async refreshTotals(cartTotalPrice) {
        const isDrawer = !!this.closest('cart-drawer');

        if (isDrawer) {
          const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          for (const selector of ['cart-drawer-items', '.cart-drawer__footer']) {
            const target = document.querySelector(selector);
            const source = html.querySelector(selector);
            if (target && source) target.replaceWith(source);
          }
        } else {
          // Use dataset.id (template-specific section ID) + sections API to get correct JSON response
          const sectionId = document.getElementById('main-cart-footer')?.dataset.id;
          if (!sectionId) return;
          const response = await fetch(`${window.routes.cart_url}?sections=${encodeURIComponent(sectionId)}`);
          const data = await response.json();
          const html = new DOMParser().parseFromString(data[sectionId] || '', 'text/html');
          const target = document.getElementById('CartPage-Totals');
          const source = html.getElementById('CartPage-Totals');
          if (target && source) target.replaceWith(source);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-discount',
          cartData: { total_price: cartTotalPrice },
        });
      }

      setLoading(state) {
        const btn = this.querySelector('[data-discount-submit]');
        const input = this.querySelector('input[name="discount"]');
        if (btn) {
          btn.disabled = state;
          btn.setAttribute('aria-busy', String(state));
        }
        if (input) input.disabled = state;
      }

      showCouponNote() {
        const note = this.querySelector('.cart-free-shipping__coupon-note');
        if (note) note.removeAttribute('hidden');
      }

      hideCouponNote() {
        const note = this.querySelector('.cart-free-shipping__coupon-note');
        if (note) note.setAttribute('hidden', '');
      }

      showError() {
        const el = this.querySelector('[data-discount-error]');
        if (!el) return;
        el.textContent =
          this.dataset.errorMessage || 'Your discount code could not be applied.';
        el.removeAttribute('hidden');
      }

      hideError() {
        const el = this.querySelector('[data-discount-error]');
        if (el) el.setAttribute('hidden', '');
      }
    }
  );
}
